import React, { useEffect } from "react";
import Modal, { ModalProps } from "@mui/joy/Modal";
import ModalDialog, { ModalDialogProps } from "@mui/joy/ModalDialog";
import DialogActions from "@mui/joy/DialogActions";
import Box from "@mui/joy/Box";
import Button, { ButtonProps } from "@mui/joy/Button";
import { Stack } from "@mui/joy";
import { ColorPaletteProp, SxProps } from "@mui/joy/styles/types";

// =================================================================
// 이 부분은 실제 프로젝트 환경에 맞게 경로를 수정해야 합니다.
import { sendMessage } from "utils/webview";
import { addAlpha } from "styles/hexOpacity";
import { theme } from "../../styles/theme";
import CustomTypography from "./CustomTypography";
import { OverridableStringUnion } from "@mui/types";
import { ButtonPropsColorOverrides } from "@mui/joy/Button/ButtonProps";
import useIsInappWebview from "../../hooks/useIsInappWebview";
// =================================================================

/**
 * 버튼의 시각적 스타일과 역할을 정의합니다.
 * 'solid': 주요 액션 (채워진 버튼)
 * 'outlined': 보조 액션 (테두리 버튼)
 * 'plain': 3차 액션 (텍스트 버튼, 예: 자세히 보기)
 */
type ModalActionVariant = "Solid" | "Soft" | "Outlined" | "Plain";

/**
 * 모달 액션 버튼의 속성을 정의하는 인터페이스입니다.
 */
export interface ModalAction {
  label: string;
  onClick: () => void;
  variant: ModalActionVariant;
  color?: OverridableStringUnion<ColorPaletteProp, ButtonPropsColorOverrides>;
  /** Joy UI Button 컴포넌트에 직접 전달할 추가 props입니다. (예: color, disabled, loading 등) */
  buttonProps?: Omit<ButtonProps, "variant" | "onClick" | "children">;
}

/**
 * CustomModal 컴포넌트의 props를 정의합니다.
 */
interface CustomModalProps {
  /** 모달의 열림 상태를 제어합니다. (필수) */
  open: boolean;
  /** 모달이 닫힐 때 호출될 함수입니다. (필수) */
  onClose: () => void;
  /** 모달의 제목입니다. (필수) */
  title: React.ReactNode;
  /** 모달의 부제(설명)입니다. 본문(children) 위에 표시됩니다. (필수) */
  subtitle?: React.ReactNode;
  /** 모달의 주 콘텐츠(본문)입니다. */
  children?: React.ReactNode;
  /**
   * 모달의 액션 버튼 배열입니다. (필수)
   * 배열에 전달된 순서대로 버튼이 표시됩니다.
   */
  actions: ModalAction[];
  /**
   * 액션 버튼 영역 아래 또는 옆에 표시될 보조적인 링크나 텍스트입니다.
   */
  footer?: React.ReactNode;
  /** Joy UI Modal 컴포넌트에 직접 전달할 props입니다. */
  modalProps?: Omit<ModalProps, "open" | "onClose" | "children">;
  /** Joy UI ModalDialog 컴포넌트에 직접 전달할 props입니다. */
  dialogProps?: Omit<ModalDialogProps, "children" | "title">;

  isMobile?: boolean;
}

export default function CustomModal({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  footer,
  modalProps,
  dialogProps,
  isMobile,
}: CustomModalProps) {
  const inappWebview = useIsInappWebview();
  const isInappWebview = isMobile ?? inappWebview;

  // 웹뷰 환경에서 네이티브 앱으로 모달 상태를 전달하는 효과
  useEffect(() => {
    const sendModalEventToApp = (status: "open" | "close") => {
      if (window.ReactNativeWebView) {
        sendMessage(JSON.stringify({ event: "modal", params: { status } }));
      }
    };
    sendModalEventToApp(open ? "open" : "close");
    return () => {
      sendModalEventToApp("close");
    };
  }, [open]);

  // dialogProps에서 sx를 분리하여 커스텀 sx와 병합
  const { sx: dialogSx, ...restDialogProps } = dialogProps || {};

  // 기본 다이얼로그 스타일
  const baseSx: SxProps = {
    px: "20px",
    pt: "32px",
    borderRadius: "24px",
    border: 0,
    boxShadow: theme.shadow.lg,
  };

  // 모바일(웹뷰) 환경 전용 스타일
  const mobileSx: SxProps = {
    textAlign: "center",
    width: "320px",
    pb: actions.length === 1 && !footer ? "20px" : "12px",
  };

  // 데스크탑 환경 전용 스타일
  const desktopSx: SxProps = {
    textAlign: "left",
    width: "480px",
    pb: "32px",
    px: "32px",
  };

  // 최종 sx를 조건부로 병합
  const finalSx = [
    baseSx,
    isInappWebview ? mobileSx : desktopSx,
    dialogSx,
  ].filter(Boolean) as SxProps;

  // 모달 닫힘 시 상위 클릭 이벤트로 버블링되지 않도록 방지
  const handleClose: ModalProps["onClose"] = (event) => {
    // React/MUI가 넘겨주는 이벤트가 있으면 전파 중지
    // (타입 안전하게 optional 체이닝 사용)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event as any)?.stopPropagation?.();
    onClose();
  };

  // actions 배열을 기반으로 버튼 컴포넌트 배열 생성
  const actionButtons = actions.map((action, index) => (
    <Button
      key={action.label + index}
      variant={action.variant}
      onClick={(e) => {
        e.stopPropagation();
        action.onClick();
      }}
      color={action.color}
      size={"md"}
      {...action.buttonProps}
      sx={{
        height: "46px",
      }}
    >
      {action.label}
    </Button>
  ));

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiModal-backdrop": {
          transition: `opacity 300ms`,
          backgroundColor: addAlpha("#000000", 0.5),
        },
      }}
      {...modalProps}
    >
      <ModalDialog
        onClick={(e) => e.stopPropagation()}
        sx={finalSx}
        {...restDialogProps}
      >
        <Stack spacing={3}>
          <Stack
            spacing={1}
            sx={{
              px: 0,
            }}
          >
            <CustomTypography
              variant={"Subtitle"}
              sx={{
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
              }}
            >
              {title}
            </CustomTypography>
            {subtitle && (
              <CustomTypography
                variant={"Body"}
                sx={{
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Medium,
                }}
              >
                {subtitle}
              </CustomTypography>
            )}
            {children && <Box pt={1}>{children}</Box>}
          </Stack>

          {isInappWebview ? (
            /* 모바일 레이아웃 */
            <Stack spacing={1}>
              {actionButtons}
              {footer && (
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    height: "32px",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {footer}
                </Box>
              )}
            </Stack>
          ) : (
            /* 데스크탑 레이아웃 */
            <Stack
              direction="row"
              sx={{
                height: "46px",
                justifyContent: "space-between",
                alignItems: "flex-end",
              }}
            >
              <Box
                sx={{
                  alignSelf: "center",
                  gap: 2,
                }}
              >
                {footer}
              </Box>

              <DialogActions sx={{ p: 0, gap: 1 }}>
                {actionButtons}
              </DialogActions>
            </Stack>
          )}
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
